import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Flag para evitar múltiples redirecciones simultáneas
let isRedirectingToLogin = false;

function handleSessionExpired() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  useUIStore.getState().addToast({
    type: 'warning',
    title: 'Session expired',
    message: 'Please log in again to continue.',
    duration: 3000,
  });

  // Limpiar estado de auth
  useAuthStore.getState().logout();

  // Redirigir después de un breve delay para que el toast sea visible
  setTimeout(() => {
    window.location.href = '/login';
    // Reset flag after redirect in case SPA navigation prevents full reload
    setTimeout(() => { isRedirectingToLogin = false; }, 2000);
  }, 1500);
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;
  const token = useAuthStore.getState().token;
  const isPublicEndpoint = endpoint.startsWith('/public/') || endpoint.startsWith('/f/') || skipAuth;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Only add auth header for non-public endpoints
  if (token && !isPublicEndpoint) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    // Only redirect to login for authenticated endpoints, not public ones
    if (response.status === 401 && !isPublicEndpoint) {
      handleSessionExpired();
      throw new ApiError(401, 'Session expired');
    }

    const error = await response.json().catch(() => ({
      message: 'An error occurred',
    }));

    // Show toast for rate limit / quota exceeded errors
    if (response.status === 429) {
      useUIStore.getState().addToast({
        type: 'warning',
        title: 'Límite alcanzado',
        message: error.message || 'Demasiadas solicitudes. Por favor, intenta más tarde.',
        duration: 5000,
      });
    }

    throw new ApiError(response.status, error.message, error);
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

// Alias for backwards compatibility
export const apiClient = api;

export { ApiError };
